/**
 * Demolition-scope predicates + the auto-pull collector. Pure: no React,
 * no canvas, no writes. A single orthogonal `demoed: true` field marks an
 * object/opening for demolition. Walls keep their own demoed-opening
 * representation (category.opening.demoed) and are NOT covered here.
 */
import {
  isOpeningWithinWallSpan,
  getObjectsOnWall,
} from './elementDrawingUtils';
import { isDemoedOpening } from './openingSyncUtils';
import { getObjectType } from './objectTypeUtils';
import { apply3DRotationTo2D } from './geometryUtils';
import { alongWallSpan } from '../components/3d/geometry/wallFaceCarve';

export const isDemoed = (el) => el?.demoed === true;

const WALL_DEPENDENT_FIXTURE_TYPES = [
  'wall-light',
  'sconce',
  'mirror',
  'switch',
  'outlet',
  'art',
  'television',
];

const FLOOR_STANDING_TYPES = [
  'cabinet',
  'vanity',
  'counter',
  'countertop',
  'appliance',
  'toilet',
  'sink',
  'island',
  'peninsula',
  'bath',
  'stove',
  'range',
  'cooktop',
  'oven',
  'dishwasher',
  'refrigerator',
  'hood',
];

const matchesType = (type, list) =>
  !!type && list.some((t) => type === t || type.includes(t));

export const isWallDependent = (el) => {
  if (!el) return false;
  if (isDemoedOpening(el)) return false; // the marker IS the demolition
  const cat = typeof el.category === 'object' && el.category ? el.category : {};
  if (cat.door || cat.window || cat.opening) return true;
  const type = getObjectType(el);
  if (type === 'door' || type === 'window' || type === 'opening') return true;
  if (matchesType(type, FLOOR_STANDING_TYPES)) return false; // exclusion wins over loose fixture match
  return matchesType(type, WALL_DEPENDENT_FIXTURE_TYPES);
};

const OPENING_KEYS = ['doors', 'windows', 'openings'];

/**
 * How far a span must reach INTO an opening to take it. A span whose end was
 * snapped onto a door's edge meets it at zero overlap, give or take float
 * noise, and must leave the door standing.
 */
const EDGE_TOLERANCE_M = 0.001;

/**
 * The structure document's doors, windows and openings a wall-demolition span
 * takes out: every one on the span's own structure wall whose extent along
 * that wall overlaps the span, even partly — a door half inside a demolished
 * stretch has no wall left to hang in. Measured on the STRUCTURE records,
 * because a scan's room copies are slim (no centre, no size) and pricing reads
 * `demoed` off these (`scanDoors.js` reads `structureDoors`).
 *
 * @param {Object} demoedSpan - the demoed-opening marker (center, dimensions,
 *   rotation, parentWallIds: [structureWallId])
 * @param {{doors?, windows?, openings?}} structure - the structure document
 * @returns {{doors: string[], windows: string[], openings: string[]}} ids
 */
export const structureOpeningsUnderSpan = (demoedSpan, structure) => {
  const out = { doors: [], windows: [], openings: [] };
  const wallId = demoedSpan?.parentWallIds?.[0];
  if (!wallId || !demoedSpan.center || !demoedSpan.dimensions) return out;
  const [dx, dz] = apply3DRotationTo2D(demoedSpan.rotation);
  const half = demoedSpan.dimensions.x / 2;
  for (const key of OPENING_KEYS) {
    for (const el of structure?.[key] || []) {
      if (!el?.id || el.parentWallIds?.[0] !== wallId) continue;
      if (isDemoed(el) || isDemoedOpening(el)) continue;
      if (!el.center || !el.dimensions) continue;
      const along = alongWallSpan(el, { x: dx, z: dz }, demoedSpan.center);
      const overlap = Math.min(along.max, half) - Math.max(along.min, -half);
      if (overlap > EDGE_TOLERANCE_M) out[key].push(el.id);
    }
  }
  return out;
};

/**
 * Room-owned elements a wall-demolition span takes out: wall-mounted fixtures
 * in `room.objects`, and any opening that exists only as a room record (no
 * `structureOpeningId` — a legacy full copy). A room copy of a STRUCTURE
 * opening is left to `structureOpeningsUnderSpan`: the mark belongs on the
 * structure record, which every reader resolves the copy against.
 */
export const collectAutoDemoedElements = (demoedSpan, rooms) => {
  if (!demoedSpan?.center || !demoedSpan?.dimensions) return [];
  const roomList = rooms instanceof Map ? Array.from(rooms.values()) : rooms;
  if (!Array.isArray(roomList)) return [];
  const result = [];
  for (const room of roomList) {
    if (!room?.id) continue;
    for (const arrayName of OPENING_KEYS) {
      const arr = room[arrayName];
      if (!Array.isArray(arr)) continue;
      for (const el of arr) {
        if (!el?.id) continue;
        if (el.structureOpeningId) continue;
        if (isDemoed(el)) continue;
        if (!isWallDependent(el)) continue;
        if (!isOpeningWithinWallSpan(el, demoedSpan)) continue;
        result.push({ roomId: room.id, arrayName, elementId: el.id });
      }
    }
    const objects = Array.isArray(room.objects) ? room.objects : [];
    const candidates = getObjectsOnWall(demoedSpan, objects);
    for (const el of candidates) {
      if (!el?.id) continue;
      if (isDemoed(el)) continue;
      if (!isWallDependent(el)) continue;
      result.push({ roomId: room.id, arrayName: 'objects', elementId: el.id });
    }
  }
  return result;
};

/**
 * Structure ids of the openings marked for demolition — read by the placement
 * overlap check, which lets a new opening replace a demolished one. A wall
 * demolition marks the STRUCTURE record (`structureOpeningsUnderSpan`); older
 * marks sit on an opening's room copies, so both are collected.
 *
 * @param {Map|Array} rooms - the room store (Map) or a room list
 * @param {{doors?, windows?, openings?}} [structure] - the structure document
 * @returns {Set<string>}
 */
export const demoedStructureOpeningIds = (rooms, structure) => {
  const ids = new Set();
  for (const key of OPENING_KEYS) {
    for (const el of structure?.[key] || []) {
      if (isDemoed(el) && el.id) ids.add(el.id);
    }
  }
  const roomList = rooms instanceof Map ? Array.from(rooms.values()) : rooms;
  for (const room of Array.isArray(roomList) ? roomList : []) {
    for (const arrayName of ['doors', 'windows', 'openings']) {
      for (const el of room?.[arrayName] || []) {
        if (isDemoed(el) && el.structureOpeningId) {
          ids.add(el.structureOpeningId);
        }
      }
    }
  }
  return ids;
};
